import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import SubscribeForm from "@/components/feature/SubscribeForm";
import useSeo from "@/hooks/useSeo";
import { categories, siteInfo } from "@/mocks/topics";

const values = [
  {
    icon: "ri-focus-3-line",
    title: "网站定位",
    desc: "不做情绪放大器，只做脉络整理者。我们服务的是想真正看懂一件事的人，而不是只想看吵架结果的人。",
  },
  {
    icon: "ri-compass-3-line",
    title: "发展理念",
    desc: "热点会过去，但理解方式会留下来。我们希望读者读完一份复盘后，获得的不只是结论，而是判断信息的方法。",
  },
  {
    icon: "ri-list-check-2",
    title: "内容原则",
    desc: "时间线优先、多源交叉、区分事实与观点、标注出处。四条原则贯穿每一篇复盘，也接受读者公开检验。",
  },
];

export default function About() {
  useSeo({
    title: "关于我们 · 黑料热点的定位、内容原则与联系方式",
    description:
      "了解黑料热点：一个以时间线与多方视角整理热点事件的复盘平台。我们公开网站定位、发展理念与内容原则，也欢迎通过邮箱或订阅表单反馈信息、补充线索。",
    keywords: "关于黑料热点,热点复盘平台,内容原则,事件时间线,联系方式",
    canonicalPath: "/about",
    ogType: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于黑料热点",
        description: "黑料热点的平台定位、发展理念、内容原则与联系方式。",
        inLanguage: "zh-CN",
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "黑料热点",
        email: siteInfo.email,
        description: "以时间线与多方视角整理热点事件的复盘内容平台。",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "/" },
          { "@type": "ListItem", position: 2, name: "关于我们", item: "/about" },
        ],
      },
    ],
  });

  return (
    <div className="min-h-screen w-full bg-background-50">
      <SiteHeader />
      <main>
        <section className="w-full border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-8 md:py-14">
            <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
            <div className="mt-6 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
              <div className="lg:col-span-6">
                <span className="text-xs font-semibold uppercase tracking-widest text-primary-600">
                  关于黑料热点
                </span>
                <h1 className="mt-3 font-heading text-3xl font-black leading-tight tracking-tight text-foreground-950 md:text-4xl">
                  {siteInfo.slogan}
                </h1>
                <p className="mt-5 text-base leading-relaxed text-foreground-700">{siteInfo.intro}</p>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                  我们由一群内容整理爱好者发起，成员来自媒体、产品与文字工作背景。
                  起初只是想在朋友圈里讲清楚一件事的来龙去脉，后来发现很多人都有同样的困惑：
                  热点明明天天刷到，却始终拼不出完整的样子。于是有了黑料热点。
                </p>
              </div>
              <div className="lg:col-span-6">
                <div className="h-64 w-full overflow-hidden rounded-lg border border-background-200 bg-background-200 md:h-80">
                  <img
                    src="https://readdy.ai/api/search-image?query=small%20team%20of%20writers%20reviewing%20printed%20documents%20at%20a%20warm%20minimal%20wooden%20table%2C%20soft%20natural%20light%2C%20editorial%20photography%2C%20muted%20tones%2C%20high%20detail&width=1200&height=800&seq=hei-about-team&orientation=landscape"
                    alt="黑料热点编辑团队在整理事件材料"
                    title="黑料热点团队 - 热点事件时间线复盘"
                    className="h-full w-full object-top object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full border-b border-background-200 bg-background-50">
          <div className="mx-auto w-full max-w-7xl px-4 py-12 md:px-8 md:py-16">
            <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
              {values.map((item) => (
                <article key={item.title} className="rounded-lg border border-background-200 bg-background-100 p-6">
                  <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className={`${item.icon} text-xl`}></i>
                  </span>
                  <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">{item.title}</h2>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full border-b border-background-200 bg-background-100">
          <div className="mx-auto grid w-full max-w-7xl grid-cols-1 gap-10 px-4 py-12 md:px-8 md:py-16 lg:grid-cols-2 lg:gap-14">
            <div>
              <h2 className="font-heading text-2xl font-bold text-foreground-950">
                一份复盘是怎么诞生的
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                从素材池到最终发布时间线，一篇复盘平均需要经过四轮核对。
                我们会优先保留原始出处，对无法确认的部分明确标注，而不是替读者下结论。
              </p>
              <ul className="mt-6 space-y-4">
                <li className="flex gap-3">
                  <i className="ri-checkbox-circle-line mt-0.5 text-xl text-secondary-600"></i>
                  <span className="text-sm leading-relaxed text-foreground-700">
                    关键事实至少比对两个独立信源，冲突之处并列呈现。
                  </span>
                </li>
                <li className="flex gap-3">
                  <i className="ri-checkbox-circle-line mt-0.5 text-xl text-secondary-600"></i>
                  <span className="text-sm leading-relaxed text-foreground-700">
                    时间线节点标注出处，方便回到原始材料核对。
                  </span>
                </li>
                <li className="flex gap-3">
                  <i className="ri-checkbox-circle-line mt-0.5 text-xl text-secondary-600"></i>
                  <span className="text-sm leading-relaxed text-foreground-700">
                    事件有新进展时更新原文，并保留修改说明。
                  </span>
                </li>
              </ul>
            </div>
            <div className="h-64 w-full overflow-hidden rounded-lg border border-background-200 bg-background-200 md:h-80 lg:h-full lg:min-h-[320px]">
              <img
                src="https://readdy.ai/api/search-image?query=close-up%20of%20hands%20annotating%20a%20printed%20timeline%20with%20a%20pen%20on%20warm%20cream%20paper%2C%20soft%20daylight%2C%20editorial%20still%20life%2C%20high%20detail&width=1000&height=750&seq=hei-about-notes&orientation=landscape"
                alt="编辑在纸面时间线上做标注"
                title="事件复盘核对流程"
                className="h-full w-full object-top object-cover"
              />
            </div>
          </div>
        </section>

        <section className="w-full border-b border-background-200 bg-background-50">
          <div className="mx-auto w-full max-w-7xl px-4 py-12 md:px-8 md:py-16">
            <h2 className="font-heading text-2xl font-bold text-foreground-950">我们覆盖的热点类型</h2>
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {categories.map((cat) => (
                <div
                  key={cat.id}
                  className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-100 p-5"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                    <i className={`${cat.icon} text-xl`}></i>
                  </span>
                  <div>
                    <h3 className="font-heading text-base font-bold text-foreground-950">{cat.name}</h3>
                    <p className="mt-1 text-sm text-foreground-600">{cat.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100">
          <div className="mx-auto grid w-full max-w-7xl grid-cols-1 gap-8 px-4 py-12 md:px-8 md:py-16 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <h2 className="font-heading text-2xl font-bold text-foreground-950">联系我们</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                无论是信息纠错、线索补充，还是内容合作，都欢迎联系我们。
                留下邮箱订阅后，你也会第一时间收到新复盘与修订提醒。
              </p>
              <div className="mt-6 space-y-4">
                <div className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-5">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                    <i className="ri-mail-line text-xl"></i>
                  </span>
                  <div>
                    <h3 className="font-heading text-sm font-bold text-foreground-950">邮箱</h3>
                    <p className="mt-1 text-sm text-foreground-600">{siteInfo.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-5">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                    <i className="ri-time-line text-xl"></i>
                  </span>
                  <div>
                    <h3 className="font-heading text-sm font-bold text-foreground-950">反馈处理</h3>
                    <p className="mt-1 text-sm text-foreground-600">
                      信息纠错通常在三个工作日内核实并更新，重大修订会保留说明。
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="lg:col-span-7">
              <SubscribeForm />
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}